// src/App.js
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import UploadVideo from "./pages/UploadVideo";
import Login from "./pages/login";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import InstituteRoute from "./components/InstituteRoute";
import Dashboard from "./pages/Dashboard";
import DashboardWrapper from "./components/DashboardWrapper";
import Register from "./pages/Register";
import RegisterInstitute from "./pages/RegisterInstitute";
import AuthHelper from "./pages/AuthHelper";
import CoursePlayer from "./pages/CoursePlayer";
import TeacherDashboard from "./pages/TeacherDashboard";
import TeacherCourseHub from "./pages/TeacherCourseHub";
import LiveClasses from "./pages/LiveClasses";
import Navbar from "./components/Navbar";
import StudentLayout from "./components/StudentLayout";
import CourseDetail from "./pages/CourseDetail";
import Home from "./pages/Home";
import OnlineExam from "./pages/OnlineExam";
import Exams from "./pages/Exams";
import TeacherExams from "./pages/TeacherExams";
import TeacherClasses from "./pages/TeacherClasses";
import TeacherLiveClasses from "./pages/TeacherLiveClasses";
import ExamResult from "./pages/ExamResult";
import MobileNav from "./components/MobileNav";
import Sidebar from "./components/Sidebar";
import Profile from "./pages/Profile";
import LandingPage from "./pages/LandingPage";
import VerifyStudent from "./pages/VerifyStudent";
import SaasLandingPage from "./pages/SaasLandingPage";
import CategoryPage from "./pages/CategoryPage";
import PublicExams from "./pages/PublicExams";
import PublicCourses from "./pages/PublicCourses";
import PurchaseCourses from "./pages/PurchaseCourses";
import PublicLiveClasses from "./pages/PublicLiveClasses";
import MyResults from "./pages/MyResults";
import AboutUs from "./pages/AboutUs";
import SuccessStories from "./pages/SuccessStories";
import ContactUs from "./pages/ContactUs";
import Cart from "./pages/Cart";
import BookReader from "./pages/BookReader";
import ParentDashboard from "./pages/ParentDashboard";
import BooksPage from "./pages/BooksPage";
import RecordingsPage from "./pages/RecordingsPage";
import CodingArena from "./pages/CodingArena";
import TeacherCodingArena from "./pages/TeacherCodingArena";
import DoubtPortal from "./pages/DoubtPortal";
import TeacherAssignments from "./pages/TeacherAssignments";
import StudentAssignments from "./pages/StudentAssignments";
import PersonalPlan from "./pages/PersonalPlan";
import SubscriptionPlans from "./pages/SubscriptionPlans";
import PlacementPreparation from "./pages/PlacementPreparation";
import ReferralAffiliate from "./pages/ReferralAffiliate";
import AITutor from "./pages/AITutor";
import BroadcastCenter from "./pages/BroadcastCenter";
import AIRevision from "./pages/AIRevision";
import DailyChallenges from "./pages/DailyChallenges";
import FriendCompetition from "./pages/FriendCompetition";
import VoiceTutor from "./pages/VoiceTutor";
import { jwtDecode } from "jwt-decode";
import TeacherSubjectiveExams from "./pages/TeacherSubjectiveExams";
import TeacherSubjectiveEvaluation from "./pages/TeacherSubjectiveEvaluation";
import StudentSubjectiveExam from "./pages/StudentSubjectiveExam";
import StudentSubjectiveExams from "./pages/StudentSubjectiveExams";


// Admin pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminInstitutes from "./pages/admin/AdminInstitutes";
import AdminSaaSPlans from "./pages/admin/AdminSaaSPlans";
import AdminMarketing from "./pages/admin/AdminMarketing";
import AdminSettings from "./pages/admin/AdminSettings";
import AnalyticsDashboard from "./pages/admin/AnalyticsDashboard";
import AdminParentLinks from "./pages/admin/AdminParentLinks";
import AdminBooks from "./pages/admin/AdminBooks";
import AdminEnquiries from "./pages/admin/AdminEnquiries";

// Institute pages
import InstituteLayout from "./pages/institute/InstituteLayout";
import InstituteDashboard from "./pages/institute/InstituteDashboard";
import InstituteUsers from "./pages/institute/InstituteUsers";
import InstituteCourses from "./pages/institute/InstituteCourses";
import InstituteOrders from "./pages/institute/InstituteOrders";
import InstituteExams from "./pages/institute/InstituteExams";
import InstituteLiveClasses from "./pages/institute/InstituteLiveClasses";
import InstituteBooks from "./pages/institute/InstituteBooks";
import InstituteAnalytics from "./pages/institute/InstituteAnalytics";
import InstituteParentLinks from "./pages/institute/InstituteParentLinks";
import InstituteSettings from "./pages/institute/InstituteSettings";
import InstituteOfflineAdmission from "./pages/institute/InstituteOfflineAdmission";
import InstituteMarketing from "./pages/institute/InstituteMarketing";
import InstituteBilling from "./pages/institute/InstituteBilling";

/**
 * SmartLandingPage — renders the SaaS sales page on the main domain
 * (skillzy.pro, www.skillzy.pro, localhost) and the institute-specific
 * LandingPage on tenant subdomains (e.g. abc.skillzy.pro).
 */
function SmartLandingPage() {
  const hostname = window.location.hostname;

  // Main domain patterns: bare domain, www prefix, or local dev
  const isMainDomain =
    hostname === "skillzy.pro" ||
    hostname === "www.skillzy.pro" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1";

  return isMainDomain ? <SaasLandingPage /> : <LandingPage />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register-institute" element={<RegisterInstitute />} />
        <Route path="/auth-helper" element={<AuthHelper />} />
        <Route path="/" element={<SmartLandingPage />} />
        <Route path="/category/:categoryName" element={<CategoryPage />} />
        <Route path="/public-exams" element={<PublicExams />} />
        <Route path="/public-courses" element={<PublicCourses />} />
        <Route path="/public-live-classes" element={<PublicLiveClasses />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/success-stories" element={<SuccessStories />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/verify/student/:studentId" element={<VerifyStudent />} />

        {/* ─── Institute routes ─── */}
        <Route
          path="/institute"
          element={
            <InstituteRoute>
              <InstituteLayout />
            </InstituteRoute>
          }
        >
          <Route index element={<InstituteDashboard />} />
          <Route path="users" element={<InstituteUsers />} />
          <Route path="courses" element={<InstituteCourses />} />
          <Route path="admission" element={<InstituteOfflineAdmission />} />
          <Route path="orders" element={<InstituteOrders />} />
          <Route path="exams" element={<InstituteExams />} />
          <Route path="live-classes" element={<InstituteLiveClasses />} />
          <Route path="books" element={<InstituteBooks />} />
          <Route path="analytics" element={<InstituteAnalytics />} />
          <Route path="parent-links" element={<InstituteParentLinks />} />
          <Route path="marketing" element={<InstituteMarketing />} />
          <Route path="broadcast" element={<BroadcastCenter isInsideAdminLayout={true} />} />
          <Route path="billing" element={<InstituteBilling />} />
          <Route path="settings" element={<InstituteSettings />} />
        </Route>

        {/* ─── Admin routes ─── */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="institutes" element={<AdminInstitutes />} />
          <Route path="saas-plans" element={<AdminSaaSPlans />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="analytics" element={<AnalyticsDashboard />} />
          <Route path="broadcast" element={<BroadcastCenter isInsideAdminLayout={true} />} />
          <Route path="marketing" element={<AdminMarketing />} />
          <Route path="enquiries" element={<AdminEnquiries />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>


        {/* ─── Full-Screen Protected Routes (No Layout) ─── */}
        <Route path="/exam/:courseId" element={<ProtectedRoute><OnlineExam /></ProtectedRoute>} />
        <Route path="/student-subjective-exam/:examId" element={<ProtectedRoute><StudentSubjectiveExam /></ProtectedRoute>} />
        <Route path="/read-book/:bookId" element={<ProtectedRoute><BookReader /></ProtectedRoute>} />

        {/* ─── Regular protected routes ─── */}
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <>
                <Navbar />
                <div className="app-main overflow-x-hidden pb-24 lg:pb-0">
                  <Routes>
                    <Route path="/home" element={<Home />} />
                    <Route path="/profile" element={<Profile />} />
                                                            <Route path="/teacher-dashboard" element={<TeacherDashboard />} />
                    <Route path="/teacher-course/:courseId" element={<TeacherCourseHub />} />
                    <Route path="/teacher-classes" element={<TeacherClasses />} />
                    <Route path="/teacher-live-classes" element={<TeacherLiveClasses />} />
                    <Route path="/teacher-exams" element={<TeacherExams />} />
                    <Route path="/teacher-subjective-exams" element={<TeacherSubjectiveExams />} />
                    <Route path="/teacher-subjective-evaluations/:examId" element={<TeacherSubjectiveEvaluation />} />
                    <Route path="/teacher-coding" element={<TeacherCodingArena />} />
                    <Route path="/upload" element={<UploadVideo />} />
                    <Route path="/course/:courseId" element={<CoursePlayer />} />
                    <Route path="/course-detail/:courseId" element={<CourseDetail />} />
                                                                                <Route path="/exam-result/:submissionId" element={<ExamResult />} />
                                                                                <Route path="/admin/parent-links" element={<AdminParentLinks />} />
                    <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
                    <Route path="/manage-books" element={
                      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
                        <Sidebar role="teacher" />
                        <div className="flex-1 lg:ml-64">
                          <AdminBooks />
                        </div>
                      </div>
                    } />
                    <Route path="/teacher-doubts" element={
                      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
                        <Sidebar role="teacher" />
                        <div className="flex-1 lg:ml-64">
                          <DoubtPortal />
                        </div>
                      </div>
                    } />
                    <Route path="/teacher-assignments" element={<TeacherAssignments />} />
                    <Route path="/teacher-broadcast" element={<BroadcastCenter />} />
                    
                    {/* ─── Student Layout Routes ─── */}
                    <Route element={<StudentLayout />}>
                      <Route path="/student-profile" element={<Profile />} />
                      <Route path="/dashboard" element={<DashboardWrapper />} />
                      <Route path="/ai-tutor" element={<AITutor />} />
                      <Route path="/exams" element={<Exams />} />
                      <Route path="/student-subjective-exams" element={<StudentSubjectiveExams />} />
                      <Route path="/my-results" element={<MyResults />} />
                      <Route path="/live-classes" element={<LiveClasses />} />
                      <Route path="/recordings" element={<RecordingsPage />} />
                      <Route path="/doubts" element={<DoubtPortal />} />
                      <Route path="/assignments" element={<StudentAssignments />} />
                      <Route path="/study-plan" element={<PersonalPlan />} />
                      <Route path="/ai-revision" element={<AIRevision />} />
                      <Route path="/daily-challenges" element={<DailyChallenges />} />
                      <Route path="/friend-competition" element={<FriendCompetition />} />
                      <Route path="/voice-tutor" element={<VoiceTutor />} />
                      <Route path="/subscription-plans" element={<SubscriptionPlans />} />
                      <Route path="/placement-preparation" element={<PlacementPreparation />} />
                      <Route path="/referrals" element={<ReferralAffiliate />} />
                      <Route path="/library" element={<BooksPage />} />
                      <Route path="/purchase-courses" element={<PurchaseCourses />} />
                      <Route path="/coding" element={<CodingArena />} />
                    </Route>
                    <Route path="/parent-dashboard" element={<ParentDashboard />} />
                    <Route path="*" element={<Home />} />
                  </Routes>
                </div>
                <MobileNav role={(() => {
                  try {
                    const token = localStorage.getItem("token");
                    return token ? jwtDecode(token).role : "student";
                  } catch { return "student"; }
                })()} />
              </>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
